import {
	type EmissionsValuesBreakdown,
	EmissionValueGranularityLevel,
	type EmissionValueItem,
	GHGScope,
	type LifecycleStage,
} from '@api-client/api'
import { groupBy, mapValues, sum, sumBy } from 'lodash-es'

export type DisplayEmissionBreakdown = Record<LifecycleStage, number>

// GHG Protocol Scope constants
export const SUB_SCOPES_3 = [
	GHGScope._3_1,
	GHGScope._3_2,
	GHGScope._3_3,
	GHGScope._3_4,
	GHGScope._3_5,
	GHGScope._3_6,
	GHGScope._3_7,
	GHGScope._3_8,
	GHGScope._3_9,
	GHGScope._3_10,
	GHGScope._3_11,
	GHGScope._3_12,
	GHGScope._3_13,
	GHGScope._3_14,
	GHGScope._3_15,
]

// All GHG Protocol scopes (Scope 1, 2, 3 and all Scope 3 subcategories)
export const ALL_GHG_SCOPES = [
	GHGScope._1_0,
	GHGScope._2_0,
	GHGScope._3_0,
	...SUB_SCOPES_3,
] as const

export const isLifecycle = (granularity?: EmissionValueGranularityLevel) =>
	granularity === EmissionValueGranularityLevel.LIFECYCLE

export const isDetailedLifecycle = (
	granularity?: EmissionValueGranularityLevel
) => granularity === EmissionValueGranularityLevel.DETAILED_LIFECYCLE

export const isCalculator = (granularity?: EmissionValueGranularityLevel) =>
	granularity === EmissionValueGranularityLevel.CALCULATOR

export const getFirstValueOrZero = (
	emissionValueItems?: readonly EmissionValueItem[]
	// This is cast due to an apparent bug in react-hook-form that returns number types as strings
) => Number(emissionValueItems?.[0]?.value ?? 0)

export const calculateCategoryTotal = (
	emissionValueItems?: readonly EmissionValueItem[]
) =>
	// Always sum: calculator-produced breakdowns split a stage into multiple line items
	// (e.g. material + inbound transport in Material Acquisition), and the displayed stage
	// total has to reflect the full sum regardless of which display mode the form is in.
	// The Number(...) cast is for an apparent bug in react-hook-form that returns numbers as strings.
	sumBy(emissionValueItems ?? [], item => Number(item.value || 0))

export const calculateSubcategoryTotals = (
	emissionValueItems?: readonly EmissionValueItem[]
) => {
	const breakdown = mapValues(
		groupBy(emissionValueItems, 'lifecycleStage'),
		// The Number is a cast due to an apparent bug in react-hook-form that returns number types as strings
		items => sumBy(items, item => Number(item.value))
	)
	return breakdown
}

export const calculateAllCategoryTotals = (
	breakdown?: EmissionsValuesBreakdown
) => {
	if (!breakdown) {
		return { material: 0, production: 0, distribution: 0 }
	}

	const material = calculateCategoryTotal(
		breakdown.materialAcquisitionAndPreprocessing
	)
	const production = calculateCategoryTotal(breakdown.production)
	const distribution = calculateCategoryTotal(breakdown.distributionAndStorage)

	return { material, production, distribution }
}

export const calculateTotalEmissions = (
	granularity?: EmissionValueGranularityLevel,
	breakdown?: EmissionsValuesBreakdown
) => {
	if (!granularity) {
		return undefined
	}
	if (isLifecycle(granularity) || isDetailedLifecycle(granularity)) {
		const totals = calculateAllCategoryTotals(breakdown)
		return sum(Object.values(totals))
	}

	// Use new field with fallback to old field for backward compatibility
	return breakdown?.valueExcludingBiogenicCarbon ?? breakdown?.value
}

/**
 * Returns the translated GHG Protocol Scope description.
 * @param scope - GHG scope (e.g., "1.0", "2.0", "3.1", "3.10")
 * @param t - Translation function from useTranslation hook
 * @returns Translated scope name, or scope value if no translation, or empty string if no scope
 */
export const getGhgCategoryByScope = (
	scope?: GHGScope,
	t?: (key: string, options?: { context?: string }) => string
): string => {
	if (!scope) {
		return ''
	}
	if (!t) {
		return scope
	}

	return t('ghgCategory.scope', { context: scope.replace('.', '_') })
}
